import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const ConnectScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (error) {
      setShake(true);
      setTimeout(() => setShake(false), 650);
    }
  }, [error]);

  const handlePinChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setPin(value);
    setError('');
    setSuccess(false);
  };

  const formatPinDisplay = (value) => {
    if (value.length <= 3) return value;
    return `${value.slice(0, 3)}-${value.slice(3)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (pin.length !== 6) {
      setError(t('connect.invalidPin'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await api.post('/screens/connect', { pin });
      
      if (response.data.success) {
        setSuccess(true);
        localStorage.setItem('screenId', response.data.screenId);
        localStorage.setItem('screenName', response.data.screenName);
        
        setTimeout(() => {
          navigate(`/waiting?screenId=${response.data.screenId}&screenName=${encodeURIComponent(response.data.screenName)}`);
        }, 800);
      }
    } catch (err) {
      console.error('Connection error:', err);
      if (err.response?.status === 404) {
        setError(t('connect.pinNotFound'));
      } else if (err.response?.status === 410) {
        setError(t('connect.pinExpired'));
      } else {
        setError(t('connect.connectionFailed'));
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E7FCC] via-[#1B6DB8] to-[#185CA4] flex items-center justify-center p-4 md:p-8">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden">
        <div className="grid md:grid-cols-[45%_55%] gap-0">
          
          {/* Left Side - Branding & Info */}
          <div className="bg-gradient-to-br from-[#1E7FCC] to-[#185CA4] p-6 md:p-10 text-white relative overflow-hidden flex flex-col justify-center">
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none" fill="none">
                <path d="M0 300 L100 200 L200 250 L300 150 L400 200 L400 400 L0 400 Z" fill="white" opacity="0.3"/>
                <path d="M0 350 L150 250 L250 280 L400 220 L400 400 L0 400 Z" fill="white" opacity="0.2"/>
              </svg>
            </div>
            
            <div className="relative z-10">
              <div className="mb-12">
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  {t('connect.title')}
                </h1>
                <p className="text-blue-50 text-xl md:text-2xl leading-relaxed font-light">
                  {t('connect.subtitle')}
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center font-bold text-xl text-[#1E7FCC] shadow-lg">
                    1
                  </div>
                  <p className="text-blue-50 text-lg leading-relaxed pt-2 font-light">
                    {t('connect.howToStep1')}
                  </p>
                </div>
                
                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center font-bold text-xl text-[#1E7FCC] shadow-lg">
                    2
                  </div>
                  <p className="text-blue-50 text-lg leading-relaxed pt-2 font-light">
                    {t('connect.howToStep2')}
                  </p>
                </div>
                
                <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center font-bold text-xl text-[#1E7FCC] shadow-lg">
                    3
                  </div>
                  <p className="text-blue-50 text-lg leading-relaxed pt-2 font-light">
                    {t('connect.howToStep3')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - PIN Input Form */}
          <div className="pt-4 pb-6 px-6 md:pt-6 md:pb-12 md:px-12 flex flex-col bg-white relative">
            {/* Logo Centered at Top - FULL SIZE */}
            <div className="mb-3 text-center">
              <img 
                src="/assets/logo/alpenscreen-ultra-transparent-512px.png" 
                alt="AlpenScreen Logo" 
                className="w-64 md:w-96 h-auto mx-auto"
              />
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-3">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl mb-4 shadow-md">
                    <svg className="w-9 h-9 text-[#1E7FCC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {t('connect.enterPin')}
                  </h2>
                  <p className="text-gray-500 text-base">
                    {t('connect.subtitle')}
                  </p>
                </div>

                <div>
                  <div className={`relative ${shake ? 'animate-shake' : ''}`}>
                    <input
                      ref={inputRef}
                      type="text"
                      id="pin"
                      value={pin}
                      onChange={handlePinChange}
                      placeholder="8UM5RZ"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`
                        w-full px-6 py-6 text-center text-4xl md:text-5xl font-mono font-bold
                        tracking-[0.5em] border-[3px] rounded-2xl
                        transition-all duration-200 uppercase
                        focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC]
                        ${error 
                          ? 'border-red-400 bg-red-50 text-red-700' 
                          : success 
                          ? 'border-green-400 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400'
                        }
                        placeholder:text-gray-400 placeholder:tracking-[0.5em]
                        disabled:bg-gray-50 disabled:cursor-not-allowed
                      `}
                      maxLength={6}
                      disabled={loading || success}
                    />
                    
                    {success && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-green-500 rounded-full p-3 animate-scale-in shadow-lg">
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between px-2">
                    <p className="text-base font-medium text-gray-500">
                      {pin.length > 0 && (
                        <span className="font-mono text-lg text-[#1E7FCC] font-bold">
                          {formatPinDisplay(pin)}
                        </span>
                      )}
                    </p>
                    <p className="text-lg text-gray-600">
                      <span className={`font-bold text-xl ${pin.length === 6 ? 'text-green-600' : 'text-gray-700'}`}>
                        {pin.length}
                      </span>
                      <span className="text-gray-500 font-semibold"> / 6</span>
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-slide-in">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || pin.length !== 6 || success}
                  className={`
                    w-full py-5 px-6 rounded-xl font-bold text-xl
                    transition-all duration-200 transform
                    focus:outline-none focus:ring-4 focus:ring-blue-500/50
                    disabled:cursor-not-allowed
                    ${success
                      ? 'bg-green-500 text-white shadow-lg'
                      : loading
                      ? 'bg-[#1E7FCC] text-white'
                      : pin.length === 6
                      ? 'bg-gradient-to-r from-[#1E7FCC] to-[#185CA4] text-white hover:from-[#1B6DB8] hover:to-[#164E91] hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0'
                      : 'bg-gray-200 text-gray-400'
                    }
                  `}
                >
                  {success ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('connect.success') || 'Erfolgreich!'}
                    </span>
                  ) : loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>{t('connect.connecting')}</span>
                    </span>
                  ) : (
                    t('connect.connectButton')
                  )}
                </button>

                <div className="text-center pt-4 border-t border-gray-100">
                  <p className="text-base text-gray-600 font-medium">
                    <svg className="w-4 h-4 inline-block mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('connect.helpText') || 'Die PIN findest du in deinem Dashboard'}
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-shake {
          animation: shake 0.65s cubic-bezier(.36,.07,.19,.97) both;
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        
        .animate-scale-in {
          animation: scale-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default ConnectScreen;